import { appVersion, env } from '@/config/env'
import { useAuthStore } from '@/store/auth'
import { useRouteStore } from '@/store/route'
import { userService } from '@services'
import axios, { InternalAxiosRequestConfig } from 'axios'
import {
  broadcastLogout,
  broadcastTokenRefresh,
  executeRefresh,
} from './token-sync'
import { appsignal } from './appsignal'

interface RequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean
}

function detectPlatform(): string {
  const ua = window.navigator.userAgent
  if (/android/i.test(ua)) return 'android'
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/edg\//i.test(ua)) return 'edge'
  if (/firefox/i.test(ua)) return 'firefox'
  if (/trident/i.test(ua)) return 'trident'
  if (/chrome/i.test(ua)) return 'blink'
  if (/safari/i.test(ua)) return 'safari'
  if (/webkit/i.test(ua)) return 'webkit'
  return 'unknown'
}

export const api = axios.create({
  baseURL: env.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config) => {
    // Skip auth header for refresh endpoint - it uses refresh token in body
    if (config.url?.includes('/refresh')) {
      return config
    }
    const { authToken } = useAuthStore.getState()
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`
    }
    config.headers.user_agent = window.navigator.userAgent
    config.headers.os = detectPlatform()
    config.headers.screen_width = window.innerWidth.toString()
    config.headers.screen_height = window.innerHeight.toString()
    config.headers.app_version = appVersion
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config as RequestConfigWithRetry

    // Debug: log the error response to see its structure
    console.log('Axios error response:', {
      status: error.response?.status,
      data: error.response?.data,
      code: error.response?.data?.code,
    })

    // Check for invalid_token_error
    if (error.response?.data?.code === 'invalid_token_error') {
      // Skip for sign-out and refresh requests
      if (
        originalRequest.url?.includes('/sign_out') ||
        originalRequest.url?.includes('/refresh')
      ) {
        return Promise.reject(error)
      }

      // Prevent infinite retry loop
      if (originalRequest._retry) {
        useAuthStore.getState().clearAuth()
        useRouteStore.getState().clearRoute()
        broadcastLogout()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      originalRequest._retry = true

      try {
        // Execute refresh with coordination (handles same-tab and cross-tab)
        const tokens = await executeRefresh(async () => {
          const refreshToken = useAuthStore.getState().refreshToken
          const result = await userService.refresh({ token: refreshToken! })

          const auth = result.authorization?.auth
          const refresh = result.authorization?.refresh
          if (!auth || !refresh) {
            throw new Error('Invalid token response')
          }

          // Update store and broadcast to other tabs
          useAuthStore.getState().setAuth(result, auth, refresh)
          broadcastTokenRefresh(auth, refresh)

          return { authToken: auth, refreshToken: refresh }
        })

        // Retry with new token
        originalRequest.headers.Authorization = `Bearer ${tokens.authToken}`
        return api(originalRequest)
      } catch {
        useAuthStore.getState().clearAuth()
        useRouteStore.getState().clearRoute()
        broadcastLogout()
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    appsignal?.sendError(error, (span) => {
      span.setAction(
        `${error.config?.method?.toUpperCase()} ${error.config?.url}`
      )
      span.setParams({
        status: error.response?.status,
        url: error.config?.url,
      })
    })

    return Promise.reject(error)
  }
)
